<template>
    <div class="container margin-t-4">
        <div id="search" class="w-100 flex relative">
            <div class="nc-input-with-clear-button">
                <input
                    v-model="query"
                    @keyup.enter="fetchResults(query)"
                    class="nc-input w-100"
                />

                <button
                    v-if="query"
                    @click="query = '';"
                    class="clear-btn"
                    type="button"
                    aria-label="Clear search"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M18 6L6 18" stroke="#465A6C" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M6 6L18 18" stroke="#465A6C" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </button>
            </div>


            <button class="btn-primary" type="button" @click="fetchResults(query)">
                Search
            </button>
        </div>

        <div class="flex">
            <div v-if="isLoading" class="loader-container">
                <loader></loader>
            </div>
            <div v-else class="w-100">
                <div v-if="!results || results.length === 0" class="margin-b-5">
                    <div class="margin-v-5">
                        <h4 class="padding-b-1">Sorry, we couldn't find any results for your search</h4>
                        <span class="text-m">Please try again with search terms like
                            <a class="cursor-pointer" @click="fetchBasePrompts('agentic AI')">agentic AI</a>,
                            <a class="cursor-pointer" @click="fetchBasePrompts('customer management')">customer management</a>, or
                            <a class="cursor-pointer" @click="fetchBasePrompts('orchestration')">orchestration</a>.
                        </span>
                    </div>
                    <div>
                        <span>You may also like</span>
                        <div v-for="item in examples" :key="item.title">
                            <SearchResultCard :item="item" />
                        </div>
                    </div>
                </div>
                <div v-else>
                    <div class="flex justify-between align-center margin-v-4 w-100">
                        <h4>Filters</h4>
                        <span> {{ resultsQuantity }} </span>
                    </div>
                    <div class="flex column-mobile gap-2">
                        <div class="filters-block flex column gap-2">
                            <div
                                v-for="collection in collections"
                                :key="collection.title"
                                class="flex align-center"
                            >
                                <input
                                    type="checkbox"
                                    class="nc-checkbox"
                                    :value="collection.handle"
                                    v-model="checkedCollections"
                                >
                                <label>{{ collection.title }}</label>
                            </div>
                        </div>

                        <div class="w-100">
                            <div class="margin-b-3 flex column gap-2 padding-l-3 border-left">
                                <div v-for="item in results" :key="item.id">
                                    <SearchResultCard :item="item" />
                                </div >
                            </div>
                            <Pagination
                                class="flex justify-center align-center"
                                :current-page="pagination.current_page"
                                :total-pages="pagination.last_page"
                                @change-page="paginate"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import {ref, onMounted, watch, computed} from 'vue'
import Pagination from "./Pagination.vue";
import SearchResultCard from "./SearchResultCard.vue";
import Loader from "./Loader.vue";


const query = ref('')
const results = ref([])
const collections = ref([])
const pagination = ref({})
const isLoading = ref(false)
const checkedCollections = ref([])
const examples = [
    {
        title: "Digital Transformation Solutions",
        description: "Netcracker is helping communication service providers globally execute successful, results-driven digital transformations to drive new business value.",
        url: "/portfolio/solutions/netcracker-digital-platform"
    },
    {
        title: "Digital Marketplaces: Seizing the B2B2X Opportunity",
        description: "CSPs can capture significant B2B2X revenue by launching digital marketplaces that bundle connectivity with partner services, but legacy infrastructure lacks the flexibility to quickly create seamless customer experiences.",
        url: "/blog/digital-marketplaces-seizing-the-b2b2x-opportunity"
    }
]
const resultsQuantity = computed(() => {
    const from = (pagination.value.current_page - 1) * pagination.value.per_page + 1
    const to = Math.min(pagination.value.current_page * pagination.value.per_page, pagination.value.total)
    return `Showing ${from} to ${to} of ${pagination.value.total} Results`
})
const buildQueryString = (q, page) => {
    const params = new URLSearchParams()
    params.append('q', q)
    params.append('page', page ? page : 1)

    checkedCollections.value.forEach(handle => {
        params.append('collections[]', handle)
    })

    return params.toString()
}

const paginate = async (page) => {
    await fetchResults(query.value, page);
}

const fetchBasePrompts = async (prompt) => {
    await fetchResults(prompt, 1);
    query.value = prompt;
}

const fetchResults = async (q, page) => {

    try {
        isLoading.value = true
        const queryString = buildQueryString(q, page)

        const res = await fetch(`api/search?${queryString}`)
        const data = await res.json()

        results.value = data.data
        collections.value = data.collections
        pagination.value = {
            last_page: data.last_page,
            current_page: data.current_page,
            total: data.total,
            per_page: data.per_page,
        }
        isLoading.value = false
    } catch (err) {
        console.error(err)
        results.value = []
        isLoading.value = false
    }
}

// const onInput = () => {
//     clearTimeout(timeout)
//     timeout = setTimeout(() => {
//         fetchResults(query.value)
//     }, 300)
// }

watch(checkedCollections, () => {

    fetchResults(query.value)
})

onMounted(() => {
    const params = new URLSearchParams(window.location.search)
    const q = params.get('q')
    if (q) query.value = q
    if (query.value) fetchResults(query.value)
})

</script>
